import { DialogModule } from '@angular/cdk/dialog';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService } from '@services/dialog.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { Router } from '@angular/router';
import { AuthService } from '@services/auth.service';
import { Session } from '@models/auth.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverlayDirective } from '@directives/overlay.directive';
import { LanguageOptions } from '@models/common.model';
import { APP_LANGUAGE_KEY } from '@constants/app.constant';

@Component({
    selector: 'pn-header',
    standalone: true,
    imports: [
        CommonModule,
        SvgIconComponent,
        OverlayModule,
        DialogModule,
        TranslateModule,
        OverlayDirective,
    ],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
    public openMenu = output<void>();
    private breakpointObserver = inject(BreakpointObserver);
    private _authService = inject(AuthService);
    private router = inject(Router);
    private dialogService = inject(DialogService);
    private translateService = inject(TranslateService);
    public isOpen = signal(false);
    public isShowDrowdown = signal(false);
    public isMobile = signal(false);
    public cdkConnectedOverlayPositions: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'center',
        },
    ];
    public session: Session | null = null;
    public languageOptions: LanguageOptions[] = [
        {
            code: 'vi',
            title: 'Tiếng Việt',
            imageUrl: '/assets/images/countries/vn.png',
            baseHref: 'vi',
        },
        {
            code: 'en',
            title: 'English',
            imageUrl: '/assets/images/countries/usa.png',
            baseHref: 'en',
        },
    ];
    public selectedLanguage!: LanguageOptions;

    constructor() {
        this.breakpointObserver
            .observe(['(max-width: 480px)', '(max-width: 1440px)'])
            .pipe(takeUntilDestroyed())
            .subscribe((value: BreakpointState) => {
                this.isMobile.set(value.breakpoints['(max-width: 480px)']);
            });

        this._authService.session.subscribe((s) => (this.session = s));
    }

    ngOnInit(): void {
        this._getLanguageSelected(this.translateService.currentLang);
        this.translateService.onLangChange.subscribe((lang) => {
            this._getLanguageSelected(lang.lang);
        });
    }

    /**
     * Get language selected
     *
     * @private
     * @param {string} lang
     * @memberof HeaderComponent
     */
    private _getLanguageSelected(lang: string) {
        const langOpt = this.languageOptions.find((l) => l.code === lang);
        this.selectedLanguage = langOpt || this.languageOptions[0];
    }

    /**
     * Handle open menu mobile
     *
     * @memberof HeaderComponent
     */
    public handleOpenMenu() {
        // this.store.dispatch(showMenuMobile({ show: true }));
        this.openMenu.emit();
    }

    /**
     * Handle click avatar
     *
     * @memberof HeaderComponent
     */
    public handleClickAvatar() {
        if (this.isMobile()) {
            this.isShowDrowdown.update((v) => !v);
        } else {
            this.isOpen.set(true);
        }
    }

    /**
     *  Handle click outside and close dropdown
     *
     * @param {*} event
     * @memberof HeaderComponent
     */
    public handleClickOutside(event: MouseEvent) {
        event.preventDefault();
        this.isShowDrowdown.update((v) => !v);
    }

    /**
     * Handle logout
     *
     * @memberof HeaderComponent
     */
    public handleLogout() {
        this._authService.logout();
        this.router.navigateByUrl('/auth/login');
    }

    /**
     * Handle open change password popup
     *
     * @memberof HeaderComponent
     */
    public handleOpenChangePasswordPopup() {
        // this.dialogService.openDialog(ChangePasswordPopupComponent);
    }

    /**
     * Handle open request credit popu p
     *
     * @memberof HeaderComponent
     */
    public handleOpenRequestCreditPopup() {
        // this.dialogService.openDialog(RequestCreditPopupComponent);
    }

    /**
     * Handle redirect to profile
     *
     * @memberof HeaderComponent
     */
    public handleRedirectToProfile() {
        this.router.navigate(['user/profile']);
    }

    /**
     * Change language
     *
     * @param {LanguageOptions} _t68
     * @memberof HeaderComponent
     */
    public changeLanguage(_t68: LanguageOptions) {
        localStorage.setItem(APP_LANGUAGE_KEY, _t68.code);
        this.translateService.use(_t68.code);
    }
}
